import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Wind } from "lucide-react";
import { http, gradeLabel } from "@/lib/api";
import { SubjectIcon } from "@/lib/subjects";
import { useProfile } from "@/context/ProfileContext";
import { GradeSelect } from "@/components/GradeSelect";

export default function SubjectPage() {
  const { subjectId } = useParams();
  const { profile } = useProfile();
  const nav = useNavigate();
  const [subject, setSubject] = useState(null);
  const [custom, setCustom] = useState("");

  useEffect(() => {
    http.get(`/subjects/${subjectId}`, { params: { grade: profile.grade } }).then((r) => setSubject(r.data));
  }, [subjectId, profile.grade, profile.state]);

  const start = (topic) => nav(`/learn/${subjectId}?topic=${encodeURIComponent(topic)}`);
  if (!subject) return <div className="max-w-5xl mx-auto px-6 py-20 text-stone-500">Loading…</div>;

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12" data-testid="subject-page">
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-[#2C4A3E]" data-testid="subject-back-link">
        <ArrowLeft className="w-4 h-4" /> All subjects
      </Link>
      <div className="mt-8 flex flex-wrap items-end gap-6 justify-between rise">
        <div className="flex items-center gap-5">
          <span className="w-16 h-16 rounded-3xl grid place-items-center" style={{ background: subject.color }}>
            <SubjectIcon name={subject.icon} className="w-7 h-7 text-white" />
          </span>
          <div>
            <h1 className="font-display text-4xl sm:text-5xl text-[#2C4A3E]" data-testid="subject-title">{subject.name}</h1>
            <p className="text-stone-500 mt-1">{subject.standard} · Grade band {subject.band}</p>
            <p className="font-display italic text-[#C87D53] mt-2" data-testid="subject-self-question">"{subject.self_question}"</p>
          </div>
        </div>
        <GradeSelect testId="subject-grade-select" />
      </div>

      <h2 className="text-xs font-semibold uppercase tracking-widest text-stone-500 mt-14">Topics for {gradeLabel(profile.grade)}</h2>
      <div className="mt-4 divide-y divide-[#E6DEC3] border-y border-[#E6DEC3]">
        {subject.topics.map((t, i) => (
          <button key={t} onClick={() => start(t)} data-testid={`topic-item-${i}`}
            className="group w-full flex items-center gap-5 py-5 text-left hover:bg-[#F7F3EB] px-3 rounded-xl transition-colors">
            <span className="font-display text-stone-400 w-8">{String(i + 1).padStart(2, "0")}</span>
            <span className="flex-1 text-lg text-stone-800">{t}</span>
            <span className="text-sm text-[#C87D53] inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <Wind className="w-4 h-4" /> Begin mindfully <ArrowRight className="w-4 h-4" />
            </span>
          </button>
        ))}
      </div>

      <form onSubmit={(e) => { e.preventDefault(); if (custom.trim()) start(custom.trim()); }} className="mt-10 flex gap-3">
        <input value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Or explore any topic you're curious about…"
          data-testid="custom-topic-input" className="flex-1 px-5 py-3 rounded-full bg-white/70 border border-[#E6DEC3] focus:outline-none focus:border-[#2C4A3E]" />
        <button type="submit" data-testid="custom-topic-submit-btn" className="px-6 py-3 rounded-full bg-[#2C4A3E] text-white hover:bg-[#1E352B] transition-colors">Start</button>
      </form>
    </div>
  );
}
